import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import "./IBClients.css";

export default function IBClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalClients, setTotalClients] = useState(0);
  const [stopCommissionLoading, setStopCommissionLoading] = useState({});
  const [enableCommissionLoading, setEnableCommissionLoading] = useState({});

  // Stop commission function
  const handleStopCommission = async (clientEmail) => {
    try {
      setStopCommissionLoading(prev => ({ ...prev, [clientEmail]: true }));
      
      const response = await axios.post('https://server.account.majestyfx.com/admin_stop_ib_commission.php', {
        email: clientEmail
      });
      
      if (response.data.success) {
        toast.success('Commission stopped successfully');
        // Refresh the data to show updated status
        fetchIBClients();
      } else {
        toast.error('Failed to stop commission');
      }
    } catch (error) {
      console.error('Error stopping commission:', error);
      toast.error('Error stopping commission');
    } finally {
      setStopCommissionLoading(prev => ({ ...prev, [clientEmail]: false }));
    }
  };

  // Enable commission function
  const handleEnableCommission = async (clientEmail) => {
    try {
      setEnableCommissionLoading(prev => ({ ...prev, [clientEmail]: true }));
      
      const response = await axios.post('https://server.account.majestyfx.com/admin_enable_ib_commission.php', {
        email: clientEmail
      });
      
      if (response.data.success) {
        toast.success('Commission enabled successfully');
        // Refresh the data to show updated status
        fetchIBClients();
      } else {
        toast.error('Failed to enable commission');
      }
    } catch (error) {
      console.error('Error enabling commission:', error);
      toast.error('Error enabling commission');
    } finally {
      setEnableCommissionLoading(prev => ({ ...prev, [clientEmail]: false }));
    }
  };

  // Fetch IB clients data from API
  const fetchIBClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://server.account.majestyfx.com/ib_clients.php');
      
      console.log('API Response:', response.data); // Debug log
      
      if (response.data.success) {
        console.log('Clients data:', response.data.clients); // Debug log
        setClients(response.data.clients);
        setTotalClients(response.data.total_clients);
      } else {
        toast.error('Failed to fetch IB clients data');
      }
    } catch (error) {
      console.error('Error fetching IB clients:', error);
      toast.error('Error fetching IB clients data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchIBClients();
  }, []);

  const exportToExcel = () => {
    if (clients.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = clients.map((client, idx) => ({
      '#': idx + 1,
      'Client Name': client.name,
      'Email': client.email,
      'Country': client.country,
      'Phone': client.phone,
      'Registration Date': client.registration_date,
      'Status': client.commission_status === '1' ? 'Active' : 'Stopped',
      'Parent Email': client.parent_email || '-',
      'Total Commission': client.total_commission || '0'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'IB Clients');
    XLSX.writeFile(wb, `IB_Clients_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel file exported successfully');
  };
  return (
    <div className="ib-clients-page">
      {/* Page Title + Breadcrumb */}
      <div className="page-header">
        <h1>IB Clients</h1>
        <div className="breadcrumb">
          <a href="/">Home</a> / IB Clients
        </div>
      </div>

      {/* Card Container */}
      <div className="card">
        <div className="card-header">
          <h3>View IB Clients</h3>
        </div>

        {/* Table Controls */}
        <div className="table-controls">
          {/* Left side (entries + buttons) */}
          <div className="left-controls">
            <div className="entries">
              <label>
                Show
                <select>
                  <option>10</option>
                  <option>25</option>
                  <option>50</option>
                </select>
                entries
              </label>
            </div>

            <div className="buttons">
              <button>Copy</button>
              <button>CSV</button>
              <button onClick={exportToExcel} disabled={loading || clients.length === 0}>Excel</button>
              <button>PDF</button>
              <button>Print</button>
              <button className="dropdown">Column visibility ▾</button>
            </div>
          </div>

          {/* Right side (search) */}
          <div className="search">
            <label>
              Search: <input type="text" />
            </label>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <table className="ib-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Client Name</th>
                <th>Email</th>
                <th>Country</th>
                <th>Phone</th>
                <th>Registration Date</th>
                <th>Status</th>
                <th>Parent Email</th>
                <th>Trade IDs</th>
                <th>Commission Earned</th>
                <th>Available Commission</th>
                <th>Withdraw Commission</th>
                <th>Profit Commission Earned</th>
                <th>Profit Commission Available</th>
                <th>Partners</th>
                <th>Commission Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="17" style={{ textAlign: 'center', padding: '20px' }}>
                    <div className="loading-spinner">Loading...</div>
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan="17" style={{ textAlign: 'center', padding: '20px' }}>
                    No IB clients found
                  </td>
                </tr>
              ) : (
                clients.map((client, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{client.client_name}</td>
                    <td>{client.client_email}</td>
                    <td>{client.country}</td>
                    <td>{client.phone}</td>
                    <td>{new Date(client.reg_date).toLocaleDateString()}</td>
                    <td>
                      <span className={`status ${client.status?.toLowerCase() || 'active'}`}>
                        {client.status || 'Active'}
                      </span>
                    </td>
                    <td>{client.parent_email || 'N/A'}</td>
                    <td>
                      {client.trade_ids && client.trade_ids.length > 0 ? (
                        <div className="trade-ids">
                          {client.trade_ids.map((id, idx) => (
                            <span key={idx} className="trade-id-badge">
                              {id}
                            </span>
                          ))}
                        </div>
                      ) : (
                        'No Trade IDs'
                      )}
                    </td>
                    <td className="commission-cell">
                      ${client.commission_earned ? parseFloat(client.commission_earned).toFixed(2) : '0.00'}
                    </td>
                    <td className="commission-cell">
                      ${client.available_commission ? parseFloat(client.available_commission).toFixed(2) : '0.00'}
                    </td>
                    <td className="commission-cell">
                      ${client.withdraw_commission ? parseFloat(client.withdraw_commission).toFixed(2) : '0.00'}
                    </td>
                    <td className="commission-cell">
                      ${client.profit_commission_earned ? parseFloat(client.profit_commission_earned).toFixed(2) : '0.00'}
                    </td>
                    <td className="commission-cell">
                      ${client.profit_commission_available ? parseFloat(client.profit_commission_available).toFixed(2) : '0.00'}
                    </td>
                    <td>
                      {client.partners && client.partners.length > 0 ? (
                        <div className="partners-list">
                          {client.partners.map((partner, idx) => (
                            <div key={idx} className="partner-email">
                              {partner}
                            </div>
                          ))}
                        </div>
                      ) : (
                        'No Partners'
                      )}
                    </td>
                    <td>
                      <span className={`commission-status ${client.commission_status?.toLowerCase() || 'active'}`}>
                        {client.commission_status || 'Active'}
                      </span>
                    </td>
                    <td>
                      {client.commission_status === 'Stopped' ? (
                        <button 
                          className="enable-commission-btn"
                          onClick={() => handleEnableCommission(client.client_email)}
                          disabled={enableCommissionLoading[client.client_email]}
                        >
                          {enableCommissionLoading[client.client_email] ? 'Enabling...' : 'Enable Commission'}
                        </button>
                      ) : (
                        <button 
                          className="stop-commission-btn"
                          onClick={() => handleStopCommission(client.client_email)}
                          disabled={stopCommissionLoading[client.client_email]}
                        >
                          {stopCommissionLoading[client.client_email] ? 'Stopping...' : 'Stop Commission'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="table-footer">
          <div>Showing {clients.length} of {totalClients} entries</div>
          <div className="pagination">
            <button disabled>Previous</button>
            <button disabled>Next</button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="footer">
        <p>
          Copyright © 2025 <span>Ample Markets</span>. All rights reserved.
        </p>
        <p className="version">Version 2.0</p>
      </div>
    </div>
  );
}

